import { MapPin, Check, ExternalLink, Info } from "lucide-react";
import { useSeo } from "@/components/Layout";
import { RevealLines, Reveal, RevealHeading, Stagger, StaggerItem } from "@/components/motion-primitives";
import { CtaButton, SectionLabel } from "@/components/ui-blocks";
import { SITE, IMAGES } from "@/data/site";
import { useLang } from "@/i18n/LanguageContext";

export default function CoachingSalle() {
  const { t } = useLang();
  const s = t.salle;
  useSeo(s.seoTitle, s.seoDesc);
  return (
    <div data-testid="coaching-salle-page">
      {/* HERO 2-col, content first */}
      <section className="max-w-8xl mx-auto px-5 sm:px-6 lg:px-12 pt-32 lg:pt-32 pb-12 lg:pb-16 grid lg:grid-cols-12 gap-8 lg:gap-14 items-center">
        <div className="lg:col-span-6">
          <SectionLabel>{s.label}</SectionLabel>
          <RevealLines as="h1" delay={0.1} className="display-hero text-ink mt-4" lines={s.heroLines} />
          <Reveal delay={0.4}><p className="mt-3 font-display text-lg font-semibold text-ink">{s.subheading}</p></Reveal>
          <Reveal delay={0.45}><p className="mt-3 text-muted-foreground leading-relaxed">{s.sub}</p></Reveal>
          <Reveal delay={0.5} className="mt-4 flex items-start gap-3 bg-secondary/70 p-4 text-sm text-muted-foreground">
            <Info size={16} className="text-accentbrand mt-0.5 shrink-0" /><p>{s.disclaimer}</p>
          </Reveal>
          <Reveal delay={0.55} className="mt-6"><CtaButton to="/contact" data-testid="salle-cta" className="w-full sm:w-auto justify-center">{s.cta}</CtaButton></Reveal>
        </div>
        <Reveal className="lg:col-span-6">
          <div className="relative aspect-[4/5] lg:aspect-[5/6] overflow-hidden bg-secondary max-w-md mx-auto lg:max-w-none">
            <img src={IMAGES.salleClient} alt={s.imgAlt} className="w-full h-full object-cover object-center" />
          </div>
        </Reveal>
      </section>

      {/* ADVANTAGES + ADDRESS/MAP */}
      <section className="bg-secondary/50 border-y border-border">
        <div className="max-w-8xl mx-auto px-5 sm:px-6 lg:px-12 py-12 lg:py-20 grid lg:grid-cols-12 gap-8 lg:gap-14">
          <div className="lg:col-span-6">
            <RevealHeading as="h2" text={s.advantagesHeading} className="display-2 text-ink" />
            <Stagger className="mt-6 grid sm:grid-cols-2 gap-x-6 gap-y-1" gap={0.05}>
              {s.advantages.map((a, i) => (
                <StaggerItem key={i} className="flex items-start gap-2.5 py-2.5 border-t border-border">
                  <Check size={16} className="text-accentbrand mt-0.5 shrink-0" /><span className="text-sm text-ink">{a}</span>
                </StaggerItem>
              ))}
            </Stagger>
          </div>
          <div className="lg:col-span-6">
            <div className="border border-border bg-card p-6">
              <SectionLabel>{s.addressLabel}</SectionLabel>
              <h3 className="mt-4 font-display text-xl font-bold text-ink">{SITE.gym.name}</h3>
              <p className="mt-2 flex items-start gap-2 text-sm text-muted-foreground"><MapPin size={16} className="text-accentbrand mt-0.5 shrink-0" /> {SITE.gym.address}</p>
              <div className="mt-4 aspect-[16/10] overflow-hidden border border-border bg-secondary" data-testid="salle-map-embed">
                <iframe title="Carte One Fitness Club Villeneuve-Loubet" src={SITE.gym.embedUrl} loading="lazy" referrerPolicy="no-referrer-when-downgrade" allowFullScreen className="w-full h-full" style={{ border: 0 }} />
              </div>
              <div className="mt-4 flex flex-col sm:flex-row gap-3">
                <a href={SITE.gym.mapsUrl} target="_blank" rel="noopener noreferrer" data-testid="salle-maps"
                  className="hover-arrow inline-flex items-center justify-center gap-2 border border-ink text-ink text-sm font-bold uppercase tracking-wide px-5 py-3 hover:bg-night hover:text-white transition-colors">
                  {s.itinerary} <ExternalLink size={14} />
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
